import { Component, OnInit } from '@angular/core';
import { DashboardMesaControlService } from '../../services/mesa-control/mesa-control.service';
import swal2 from 'sweetalert2';
import * as ExcelJS from 'exceljs/dist/exceljs.min.js';
import * as FileSaver from 'file-saver';

@Component({
  selector: 'app-mesa-control',
  templateUrl: './mesa-control.component.html',
  styleUrls: ['./mesa-control.component.css']
})
export class DashboardMesaControlComponent implements OnInit {

  fechaConsulta: string;
  operaciones: any[] = [];
  banxicoRates: any = null;
  loading = false;

  // Tabla tasas por cadena y banco (endpoint company_bank_rates)
  companyBankRates: any[] = [];
  companyBankRatesFiltradas: any[] = [];
  companyBancosList: string[] = [];

  // KPIs del grid
  totalOperaciones = 0;
  opsCorrectos = 0;
  opsDiscrepancias = 0;

  constructor(private mesaControlService: DashboardMesaControlService) {}

  ngOnInit() {
    const today = new Date();
    this.fechaConsulta = this.formatDate(today);

    // Cargar tabla cadenas-bancos (no depende de fecha, una sola vez)
    this.cargarCompanyBankRates();
  }

  // ==================== NAVEGACIÓN DE FECHA ====================
  cambiarFecha(dias: number) {
    if (!this.fechaConsulta) { return; }
    const parts = this.fechaConsulta.split('-');
    const fecha = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    fecha.setDate(fecha.getDate() + dias);
    this.fechaConsulta = this.formatDate(fecha);
    this.consultar();
  }

  formatDate(d: Date): string {
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    const year = d.getFullYear();
    if (month.length < 2) { month = '0' + month; }
    if (day.length < 2) { day = '0' + day; }
    return [year, month, day].join('-');
  }

  // ==================== CONSULTAR ====================
  consultar() {
    if (!this.fechaConsulta) {
      swal2.fire('Error', 'Seleccione una fecha', 'error');
      return;
    }

    this.loading = true;
    swal2.fire({ title: 'Cargando datos...', allowOutsideClick: false });
    swal2.showLoading();

    let completados = 0;
    const totalLlamadas = 2;
    const errores = [];

    const checkComplete = () => {
      completados++;
      if (completados >= totalLlamadas) {
        this.loading = false;
        swal2.close();
        if (errores.length > 0) {
          swal2.fire('Advertencia', 'Algunos datos no pudieron cargarse: ' + errores.join(', '), 'warning');
        }
        if (this.operaciones.length === 0) {
          swal2.fire('Sin datos', 'No se encontraron operaciones para la fecha: ' + this.fechaConsulta, 'info');
        }
        this.calcularKPIs();
      }
    };

    // 1. Reporte extendido (grid de validación)
    this.mesaControlService.getDailyOperationsExtended(this.fechaConsulta, 'SI').subscribe(
      resp => { this.operaciones = resp || []; checkComplete(); },
      err => { this.operaciones = []; errores.push('Operaciones'); checkComplete(); }
    );

    // 2. Tasas Banxico
    this.mesaControlService.getBanxicoRates(this.fechaConsulta).subscribe(
      resp => { this.banxicoRates = resp; checkComplete(); },
      err => { this.banxicoRates = null; errores.push('Tasas Banxico'); checkComplete(); }
    );
  }

  // ==================== KPIs ====================
  calcularKPIs() {
    this.totalOperaciones = this.operaciones.length;
    this.opsCorrectos = this.operaciones.filter(op => op.estado_final === true).length;
    this.opsDiscrepancias = this.operaciones.filter(op => op.estado_final === false).length;
    this.filtrarTasasPorCadena();
  }

  // ==================== FILTRAR TASAS POR CADENAS DEL DÍA ====================
  filtrarTasasPorCadena() {
    if (this.companyBankRates.length === 0 || this.operaciones.length === 0) {
      this.companyBankRatesFiltradas = this.companyBankRates;
      return;
    }
    // Obtener cadenas/deudores únicos de las operaciones del día
    const cadenasDelDia = new Set<string>();
    this.operaciones.forEach(op => {
      if (op.deudor) { cadenasDelDia.add(op.deudor.toUpperCase().trim()); }
    });
    // Filtrar solo las que coinciden
    this.companyBankRatesFiltradas = this.companyBankRates.filter(cadena =>
      cadenasDelDia.has(cadena.nombre.toUpperCase().trim())
    );
    // Si no hay match, mostrar todas como fallback
    if (this.companyBankRatesFiltradas.length === 0) {
      this.companyBankRatesFiltradas = this.companyBankRates;
    }
  }

  // ==================== CARGAR TASAS POR CADENA Y BANCO ====================
  cargarCompanyBankRates() {
    this.mesaControlService.getCompanyBankRates().subscribe(
      resp => {
        if (resp && resp.columnas_fondeo && resp.cadenas) {
          this.companyBancosList = resp.columnas_fondeo;
          this.companyBankRates = resp.cadenas;
        } else if (resp && resp.bancos && resp.cadenas) {
          // Fallback al formato anterior
          this.companyBancosList = resp.bancos;
          this.companyBankRates = resp.cadenas;
        }
      },
      err => {
        this.companyBankRates = [];
        this.companyBancosList = [];
      }
    );
  }

  // ==================== EXPORTAR EXCEL GENERAL ====================
  exportExcelGeneral() {
    if (this.operaciones.length === 0) {
      swal2.fire('Sin datos', 'No hay datos para exportar', 'warning');
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Operación del día');

    const headers = [
      'No. Oper.', 'No. Control', '# facturas', 'Proveedor', 'DEUDOR',
      'Fecha salida', 'Fecha vence', 'días', 'Importe', 'Moneda',
      'TIIE', 'Sobre tasa', 'Tasa Factor', 'Interés Factor',
      'Monto Importe menos intereses', 'Monto por Disposición', 'Agrupador', '', 'BANCO'
    ];

    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
      cell.alignment = { horizontal: 'center' };
    });

    this.operaciones.forEach((op, index) => {
      const row = [
        index + 1,
        op.folio_solicitud || '',
        op.folio_factura || '',
        op.proveedor || '',
        op.deudor || '',
        op.fecha_operacion || '',
        op.fecha_vencimiento || '',
        op.dias_factoraje || '',
        op.importe != null ? parseFloat(op.importe) : '',
        op.moneda || '',
        op.tasa_interbancaria != null ? (parseFloat(op.tasa_interbancaria) / 100) : '',
        op.sobre_tasa != null ? (parseFloat(op.sobre_tasa) / 100) : '',
        op.tasa_factoraje != null ? (parseFloat(op.tasa_factoraje) / 100) : '',
        op.intereses_factoraje != null ? parseFloat(op.intereses_factoraje) : '',
        op.importe_sin_intereses_factoraje != null ? parseFloat(op.importe_sin_intereses_factoraje) : '',
        op.por_disposicion_solicitud || '',
        op.folio_solicitud || '',
        '',
        op.fondeador || ''
      ];
      worksheet.addRow(row);
    });

    worksheet.columns.forEach((col, i) => { col.width = i === 3 || i === 4 ? 30 : 18; });
    worksheet.getColumn(11).numFmt = '0.0000%';
    worksheet.getColumn(12).numFmt = '0.00%';
    worksheet.getColumn(13).numFmt = '0.0000%';
    worksheet.getColumn(9).numFmt = '$#,##0.00';
    worksheet.getColumn(14).numFmt = '$#,##0.00';
    worksheet.getColumn(15).numFmt = '$#,##0.00';

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      FileSaver.saveAs(blob, `FGG_Operacion_del_dia_${this.fechaConsulta}.xlsx`);
    });
  }

  // ==================== EXPORTAR EXCEL POR FONDEADOR ====================
  exportExcelByFunder() {
    if (this.operaciones.length === 0) {
      swal2.fire('Sin datos', 'No hay datos para exportar', 'warning');
      return;
    }

    // Agrupar por fondeador localmente
    const funderMap = {};
    this.operaciones.forEach(op => {
      const funder = op.fondeador || 'Sin Fondeador';
      if (!funderMap[funder]) { funderMap[funder] = []; }
      funderMap[funder].push(op);
    });

    const workbook = new ExcelJS.Workbook();

    const funderHeaders = [
      'Disposición', '# facturas', 'Proveedor', 'DEUDOR',
      'Fecha salida', 'Fecha vence', 'días', 'Importe', 'Moneda',
      'TIIE DE DÍA', 'Sobre tasa', 'Tasa', 'Interés', 'Monto Importe menos intereses'
    ];

    Object.keys(funderMap).forEach(funderName => {
      const sheetName = funderName.substring(0, 31);
      const worksheet = workbook.addWorksheet(sheetName);

      const headerRow = worksheet.addRow(funderHeaders);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
        cell.alignment = { horizontal: 'center' };
      });

      const ops = funderMap[funderName];
      let totalImporte = 0;
      let totalInteres = 0;
      let totalNeto = 0;

      ops.forEach(op => {
        const importe = parseFloat(op.importe) || 0;
        const interes = parseFloat(op.intereses_fondeo) || 0;
        const neto = parseFloat(op.importe_sin_intereses_fondeo) || 0;
        totalImporte += importe;
        totalInteres += interes;
        totalNeto += neto;

        worksheet.addRow([
          op.folio_solicitud || '',
          op.folio_factura || '',
          op.proveedor || '',
          op.deudor || '',
          op.fecha_operacion || '',
          op.fecha_vencimiento || '',
          op.dias_fondeo || '',
          importe,
          op.moneda || '',
          op.tasa_interbancaria_fondeo != null ? (parseFloat(op.tasa_interbancaria_fondeo) / 100) : '',
          op.sobre_tasa_fondeo != null ? (parseFloat(op.sobre_tasa_fondeo) / 100) : '',
          op.tasa_total_fondeo != null ? (parseFloat(op.tasa_total_fondeo) / 100) : '',
          interes,
          neto
        ]);
      });

      // Fila totales
      const totalesRow = worksheet.addRow([
        '', '', '', '', '', '', '', totalImporte, '', '', '', '', totalInteres, totalNeto
      ]);
      totalesRow.eachCell((cell) => { cell.font = { bold: true }; });

      worksheet.columns.forEach((col, i) => { col.width = i === 2 || i === 3 ? 30 : 18; });
      worksheet.getColumn(10).numFmt = '0.0000%';
      worksheet.getColumn(11).numFmt = '0.00%';
      worksheet.getColumn(12).numFmt = '0.00%';
      worksheet.getColumn(8).numFmt = '$#,##0.00';
      worksheet.getColumn(13).numFmt = '$#,##0.00';
      worksheet.getColumn(14).numFmt = '$#,##0.00';
    });

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      FileSaver.saveAs(blob, `FGG_Operacion_PorFondeador_${this.fechaConsulta}.xlsx`);
    });
  }

  // ==================== FORMATOS ====================
  formatCurrency(value: any): string {
    if (value === null || value === undefined || value === '') { return '$0.00'; }
    const num = parseFloat(value);
    if (isNaN(num)) { return '$0.00'; }
    return '$' + num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  formatPercent(value: any): string {
    if (value === null || value === undefined || value === '') { return '0.00%'; }
    const num = parseFloat(value);
    if (isNaN(num)) { return '0.00%'; }
    return num.toFixed(2) + '%';
  }

  formatPercentBanxico(value: any): string {
    if (value === null || value === undefined || value === '') { return '0.0000%'; }
    const num = parseFloat(value);
    if (isNaN(num)) { return '0.0000%'; }
    return num.toFixed(4) + '%';
  }
}
